import { type Board, blockedAt, boardSeed, freeArrows, generate, occupancy, ray } from "./board";
import { Rng } from "./rng";
import { type Spec, DEFAULT_SPEC, generateShared } from "./shared";

/**
 * مسابقه‌ی فلش‌ها (docs/arrow-race.md): همه همان سه تخته را دارند و هر کس تخته‌اش را تمام کند، تخته‌ی
 * بعدی بی‌درنگ برایش می‌آید. لمسِ فلشِ بسته ۲ ثانیه یخ می‌زند. برنده اولین کسی است که هر سه را
 * تمام کند.
 *
 * همه‌ی زمان‌ها میلی‌ثانیه‌اند و از بیرون داده می‌شوند (now)، تا آزمون بی ساعتِ واقعی کلِ مسابقه را
 * بدَوانَد. این‌ها آینه‌ی میزِ سرورِ آینده‌اند (RaceTable).
 */

export const BOARDS = 3;

/**
 * solo: هر کس سه تخته‌ی خودش را پشتِ هم. shared: یک تخته‌ی بزرگ برای همه، هر کس رنگِ خودش (shared.ts)؛
 * آن‌جا alive ِ همه‌ی دونده‌ها یک آرایه است و هر کس فقط فلش‌های رنگِ خودش را می‌زند.
 */
export type Mode = "solo" | "shared";
export const FREEZE_MS = 2000;
/** وقتی نفرِ اول تمام کرد، بقیه این‌قدر وقت دارند. */
export const GRACE_MS = 30_000;
/** سقفِ کلِ مسابقه. */
export const CAP_MS = 240_000;
/** مکثِ میانِ دو تخته (جشنِ کوچکِ تمام‌کردنِ تخته): برای همه یکی است. */
export const BOARD_BREAK_MS = 700;

export type Level = "easy" | "medium" | "hard";

/**
 * ربات همان کارِ آدم را می‌کند: یک فلشِ آزاد برمی‌دارد. سختی فقط در سرعت و اشتباه است.
 * find: ثانیه‌ی «پیدا کردن» ِ فلشِ آزاد؛ وقتی فلشِ آزاد کم است بیشتر طول می‌کشد (bot delay).
 * miss: احتمالِ لمسِ فلشِ بسته. عددها حدس‌اند و باید با زمانِ آدم‌های واقعی تنظیم شوند.
 */
export interface Bot { find: number; miss: number }

export const BOT: Record<Level, Bot> = {
  easy: { find: 0.9, miss: 0.1 },
  medium: { find: 0.51, miss: 0.05 },
  hard: { find: 0.27, miss: 0.02 },
};
/** زمانِ دست: از دیدن تا لمس. */
export const HAND_S = 0.35;

/** ربات با نامِ پله (سه تخته) یا با عددهای خودش (مرحله‌های تخته‌ی مشترک). */
const skill = (b: Level | Bot): Bot => (typeof b === "string" ? BOT[b] : b);

export interface Runner {
  name: string;
  bot: Level | Bot | null;
  /** تخته‌ی فعلی؛ BOARDS = هر سه تمام شد. */
  board: number;
  /** فلش‌های خودش، روی هم (تیمی: سهمِ هم‌تیمی‌ها شاید برابر نباشد). */
  total: number;
  alive: boolean[];
  left: number;
  cleared: number;
  mistakes: number;
  frozenUntil: number;
  /** زمانِ آخرین فلشِ برداشته، برای شکستنِ تساوی. */
  lastClear: number;
  /** میلی‌ثانیه از «برو» تا تمام‌کردن؛ null = هنوز. */
  finishedAt: number | null;
  /** ربات: زمانِ لمسِ بعدی. */
  nextAt: number;
  /** تیمی: فلش‌های یار که بعد از تمام‌کردنِ خودش زد. */
  helped: number;
}

export interface Race {
  seed: number;
  mode: Mode;
  boards: Board[];
  /** شمارِ فلش‌های بازیکنِ ۰ (solo: هر سه تخته؛ shared: فلش‌های رنگِ او). سهمِ هر کس در Runner.total است. */
  total: number;
  /** تیمی: صندلیِ ۰ و ۲ یک تیم، ۱ و ۳ تیمِ دیگر؛ تیمی می‌بَرد که هر دو نفرش زودتر تمام کنند. */
  teams: boolean;
  start: number;
  runners: Runner[];
  firstFinish: number | null;
  over: boolean;
  freezeMs: number;
  rng: Rng;
}

export function newRace(seed: number, players: { name: string; bot: Level | Bot | null }[], start: number,
  freezeMs = FREEZE_MS, mode: Mode = "solo", spec: Spec = DEFAULT_SPEC): Race {
  const boards = mode === "shared"
    ? [generateShared(seed, players.length === 4 ? 4 : 2, spec)]
    : Array.from({ length: BOARDS }, (_, i) => generate(boardSeed(seed, i), i));
  const shared = mode === "shared" ? boards[0]!.arrows.map(() => true) : null;
  const race: Race = {
    seed, mode, boards, start,
    total: shared ? boards[0]!.arrows.filter((a) => a.owner === 0).length : boards.reduce((n, b) => n + b.arrows.length, 0),
    teams: mode === "shared" && !!spec.teams,
    runners: [], firstFinish: null, over: false, freezeMs, rng: new Rng(seed + 0x5eed),
  };
  race.runners = players.map((p, i) => {
    const r: Runner = {
      name: p.name, bot: p.bot, board: 0, total: 0, alive: [], left: 0, cleared: 0, mistakes: 0,
      frozenUntil: 0, lastClear: start, finishedAt: null, nextAt: start, helped: 0,
    };
    enter(r, boards[0]!, i, shared);
    r.total = shared ? r.left : race.total;
    if (p.bot) r.nextAt = start + botDelay(p.bot, ownFree(boards[0]!, r.alive, i).length, race.rng);
    return r;
  });
  return race;
}

function enter(r: Runner, b: Board, i: number, shared: boolean[] | null = null): void {
  r.alive = shared ?? b.arrows.map(() => true);
  r.left = b.arrows.filter((a) => mine(a.owner, i)).length;
}

/** فلشِ بی‌صاحب (تخته‌ی تک‌نفره) مالِ همه است. */
const mine = (owner: number | undefined, i: number) => owner === undefined || owner === i;

/** فلش‌های آزادِ دونده‌ی i. */
function ownFree(b: Board, alive: readonly boolean[], i: number): number[] {
  return freeArrows(b, alive).filter((id) => mine(b.arrows[id]!.owner, i));
}

export type Tap =
  /** بیرون رفت. dist: خانه‌های سرِ راهِ نوک تا لبه. */
  | { kind: "out"; arrow: number; board: number; dist: number; boardDone: boolean; finished: boolean }
  /** به مانع خورد. dist: چند خانه تا مانع (۱ = کناری). */
  | { kind: "blocked"; arrow: number; board: number; dist: number }
  | { kind: "frozen" }
  | { kind: "none" };

/**
 * فلش‌های چه کسی را دونده‌ی i الان می‌زند: تا تمام نکرده مالِ خودش؛ بعد در تیمی مالِ یارش (روبه‌رو، i ^ 2) تا او هم
 * تمام کند (کاربر، ۷ آبان: «وقتی یک نفر همه‌ی فلش‌هایش را تمام کرد می‌تواند به یارش کمک کند»)؛ −۱ = هیچ.
 */
export function target(race: Race, i: number): number {
  const r = race.runners[i];
  if (!r) return -1;
  if (r.finishedAt === null) return i;
  const m = race.runners[i ^ 2];
  return race.teams && m && m.finishedAt === null ? i ^ 2 : -1;
}

/** لمسِ فلشِ arrow روی تخته‌ی فعلیِ دونده‌ی i (تیمی، بعد از تمام‌کردن: فلشِ یارش). */
export function tap(race: Race, i: number, arrow: number, now: number): Tap {
  const r = race.runners[i];
  const who = target(race, i);
  if (!r || race.over || who < 0) return { kind: "none" };
  if (now < r.frozenUntil) return { kind: "frozen" };
  // فلش مالِ صاحبش برداشته می‌شود، حتی اگر یارش زده باشد؛ لمسِ اشتباه کسی را یخ می‌زند که زده
  const o = race.runners[who]!;
  const b = race.boards[o.board]!;
  const a = b.arrows[arrow];
  if (!a || !o.alive[arrow] || !mine(a.owner, who)) return { kind: "none" };
  const block = blockedAt(b, occupancy(b, o.alive), a);
  if (block > 0) {
    r.mistakes++;
    r.frozenUntil = now + race.freezeMs;
    return { kind: "blocked", arrow, board: o.board, dist: block };
  }
  const dist = ray(b, a).length;
  o.alive[arrow] = false;
  o.left--;
  o.cleared++;
  o.lastClear = now;
  if (who !== i) r.helped++;
  const board = o.board;
  let boardDone = false, finished = false;
  if (o.left === 0) {
    boardDone = true;
    o.board++;
    if (o.board >= race.boards.length) {
      o.finishedAt = now - race.start;
      finished = true;
      if (!race.teams || teamDone(race, who)) race.firstFinish ??= now;
    } else {
      enter(o, race.boards[o.board]!, who);
      // تخته‌ی تازه پس از مکثِ میانِ دو تخته دیده می‌شود؛ تا آن وقت لمس ندارد.
      o.frozenUntil = now + BOARD_BREAK_MS;
    }
  }
  settle(race, now);
  return { kind: "out", arrow, board, dist, boardDone, finished };
}

/**
 * پایان: همه‌ی آدم‌ها تمام کردند، یا GRACE_MS از تمام‌کردنِ نفرِ اول گذشت، یا سقفِ CAP_MS. ربات کسی
 * را منتظر نگه نمی‌دارد: وقتی آدم‌ها تمام کردند، مسابقه تمام است. (بی آدم، همه باید تمام کنند.)
 */
export function settle(race: Race, now: number): boolean {
  if (race.over) return true;
  const who = race.runners.map((_, i) => i).filter((i) => !race.runners[i]!.bot);
  const all = who.length ? who : race.runners.map((_, i) => i);
  // تیمی: آدم وقتی کارش تمام است که تیمش تمام کرده باشد — هم‌تیمیِ ربات هم باید برسد.
  const done = all.every((i) => (race.teams ? teamDone(race, i) : race.runners[i]!.finishedAt !== null));
  if (done || (race.firstFinish !== null && now - race.firstFinish >= GRACE_MS) || now - race.start >= CAP_MS) {
    race.over = true;
  }
  return race.over;
}

/** مکثِ ربات پیش از لمسِ بعدی، با free فلشِ آزاد روی تخته. */
export function botDelay(level: Level | Bot, free: number, rng: Rng): number {
  const find = skill(level).find * (0.6 + 0.8 * rng.value()) * (1 + 1.5 / Math.max(1, free));
  return Math.round((HAND_S + find) * 1000);
}

/** ربات‌هایی که وقتشان رسیده لمس می‌کنند — رباتِ تمام‌کرده در تیمی به یارش کمک می‌کند. */
export function stepBots(race: Race, now: number): { runner: number; tap: Tap }[] {
  const out: { runner: number; tap: Tap }[] = [];
  race.runners.forEach((r, i) => {
    const who = target(race, i);
    if (!r.bot || race.over || who < 0 || now < r.nextAt || now < r.frozenUntil) return;
    const o = race.runners[who]!;
    const b = race.boards[o.board]!;
    const free = ownFree(b, o.alive, who);
    const blocked = b.arrows.filter((a) => o.alive[a.id] && mine(a.owner, who) && !free.includes(a.id)).map((a) => a.id);
    const far = blocked.length ? farBlocked(b, o.alive, blocked) : blocked;
    const pick = blocked.length && race.rng.chance(skill(r.bot).miss)
      ? far[race.rng.next(far.length)]!
      : free[race.rng.next(free.length)]!;
    const t = tap(race, i, pick, now);
    out.push({ runner: i, tap: t });
    const next = target(race, i);
    if (next >= 0) {
      const n = race.runners[next]!;
      r.nextAt = Math.max(now, r.frozenUntil) + botDelay(r.bot, ownFree(race.boards[n.board]!, n.alive, next).length, race.rng);
    }
  });
  settle(race, now);
  return out;
}

/**
 * اشتباهِ ربات مثلِ آدم: فلشی که مانعش دورتر سرِ راه است و آزاد به نظر می‌آید — از بسته‌ها آن‌هایی که مانعشان
 * دست‌کم سه خانه دور است، اگر نبود دو خانه، اگر نبود همه (آینه‌ی farBlocked ِ race.go).
 */
function farBlocked(b: Board, alive: readonly boolean[], blocked: number[]): number[] {
  const occ = occupancy(b, alive);
  for (const min of [3, 2]) {
    const far = blocked.filter((id) => blockedAt(b, occ, b.arrows[id]!) >= min);
    if (far.length) return far;
  }
  return blocked;
}

/** تیمی: هر دو نفرِ تیمِ صندلیِ i تمام کرده‌اند؟ */
export function teamDone(race: Race, i: number): boolean {
  return [i % 2, (i % 2) + 2].every((k) => race.runners[k]?.finishedAt != null);
}

/**
 * رتبه‌ها: تمام‌کرده‌ها به ترتیبِ زمان، بعد بقیه به شمارِ فلش (برابر = کسی که زودتر به آن رسید). تیمی: اول تیمِ
 * برنده (زمانِ تیم = دیرترین هم‌تیمی؛ نرسیده‌ها به شمارِ فلشِ هر دو)، و در هر تیم همان ترتیبِ تک‌نفره.
 */
export function ranking(race: Race): number[] {
  if (race.teams) {
    const of = (t: number) => {
      const rs = [race.runners[t]!, race.runners[t + 2]!];
      const fin = rs.every((r) => r.finishedAt !== null) ? Math.max(...rs.map((r) => r.finishedAt!)) : null;
      return { fin, cleared: rs[0]!.cleared + rs[1]!.cleared, last: Math.max(rs[0]!.lastClear, rs[1]!.lastClear) };
    };
    const [a, b] = [of(0), of(1)];
    const first = a.fin !== null || b.fin !== null
      ? (b.fin === null || (a.fin !== null && a.fin <= b.fin) ? 0 : 1)
      : (a.cleared !== b.cleared ? (a.cleared > b.cleared ? 0 : 1) : (a.last <= b.last ? 0 : 1));
    const solo = solos(race);
    return [first, 1 - first].flatMap((t) => solo.filter((i) => i % 2 === t));
  }
  return solos(race);
}

function solos(race: Race): number[] {
  return race.runners.map((_, i) => i).sort((x, y) => {
    const a = race.runners[x]!, b = race.runners[y]!;
    if (a.finishedAt !== null || b.finishedAt !== null) {
      if (a.finishedAt === null) return 1;
      if (b.finishedAt === null) return -1;
      return a.finishedAt - b.finishedAt;
    }
    return b.cleared - a.cleared || a.lastClear - b.lastClear;
  });
}
